import { getUserCountryLabel } from '../data/countries';
import { getUserDisplayName } from './properties';

const csvEscape = (value) => {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const formatWalletTxType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'management_fee') return 'Management fee';
  if (t === 'deposit') return 'Deposit';
  if (t === 'investment') return 'Investment';
  if (t === 'sell_primary_return') return 'Sell (return to offering)';
  if (t === 'rental_payout' || t === 'dividend' || t === 'distribution') return 'Distribution';
  if (t === 'marketplace_buy') return 'Marketplace buy';
  if (t === 'marketplace_sell') return 'Marketplace sell';
  return type || 'Transaction';
};

/** Download a single user's full admin profile as CSV (profile + portfolio + wallet rows). */
export const downloadRegisteredUserProfileCsv = (detail) => {
  if (!detail?.user) return;
  const user = detail.user;
  const summary = detail.portfolioSummary || {};
  const investments = detail.investments || [];
  const transactions = detail.walletTransactions || [];

  const profileRow = {
    section: 'profile',
    uid: user.uid || '',
    email: user.email || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    displayName: getUserDisplayName(user),
    country: user.countryLabel || getUserCountryLabel(user.country) || user.country || '',
    currency: user.currency || '',
    phone: user.phone || '',
    role: user.role || 'investor',
    kycStatus: user.kycStatus || '',
    accountStatus: user.isActive === false ? 'blocked' : 'active',
    registeredAt: user.createdAt || '',
    verifiedAt: detail.kycVerifiedAt || '',
    verifiedBy: detail.kycReviewerLabel || '',
    profilePhotoUrl: detail.profilePhotoUrl || user.avatar || '',
    walletBalanceUsd: summary.walletBalanceUsd ?? user.balance ?? 0,
    portfolioInvestedUsd: summary.totalInvestedUsd ?? 0,
    holdingsCount: summary.holdingsCount ?? 0,
    propertyTitle: '',
    shares: '',
    investedUsd: '',
    transactionDate: '',
    transactionType: '',
    transactionAmountUsd: '',
  };

  const holdingRows = investments.map((inv) => ({
    ...profileRow,
    section: 'holding',
    propertyTitle: inv.propertyTitle || '',
    shares: inv.shares ?? '',
    investedUsd: inv.investedUsd ?? '',
    transactionDate: inv.purchaseDate || '',
  }));

  const txRows = transactions.map((tx) => ({
    ...profileRow,
    section: 'wallet_transaction',
    transactionDate: tx.createdAt || '',
    transactionType: formatWalletTxType(tx.type),
    transactionAmountUsd: tx.amountUsd ?? '',
  }));

  const headers = [
    'section',
    'uid',
    'email',
    'firstName',
    'lastName',
    'displayName',
    'country',
    'currency',
    'phone',
    'role',
    'kycStatus',
    'accountStatus',
    'registeredAt',
    'verifiedAt',
    'verifiedBy',
    'profilePhotoUrl',
    'walletBalanceUsd',
    'portfolioInvestedUsd',
    'holdingsCount',
    'propertyTitle',
    'shares',
    'investedUsd',
    'transactionDate',
    'transactionType',
    'transactionAmountUsd',
  ];

  const rows = [profileRow, ...holdingRows, ...txRows];
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach((row) => {
    lines.push(headers.map((header) => csvEscape(row[header])).join(','));
  });

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = (user.uid || 'user').slice(0, 12);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `coown-user-profile-${slug}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
